import {
  SerializedError,
  createSlice,
  isRejectedWithValue,
} from "@reduxjs/toolkit";
import { IUserView } from "../../modules/users/models/IUserView";
import * as UserActions from "./users.actions";
import { ToastUtil } from "../../utils/ToastUtil";
import { TokenUtil } from "../../utils/TokenUtil";
import { IAddressView } from "../../modules/users/models/IAddressView";

export const userFeatureKey = "userFeature";

export interface InitialState {
  loading: boolean;
  error: SerializedError;
  user: IUserView;
  users: IUserView[];
  token: string;
  isAuthenticated: boolean;
  address: IAddressView;
}

const initialState: InitialState = {
  loading: false,
  error: {} as SerializedError,
  user: {} as IUserView,
  users: [] as IUserView[],
  token: "",
  isAuthenticated: false,
  address: {} as IAddressView,
};

export const userSlice = createSlice({
  name: "userSlice",
  initialState: initialState,
  reducers: {
    userLogOutAction: (state, action) => {
      state.isAuthenticated = false;
      state.user = {} as IUserView;
      TokenUtil.deleteToken(); //Remove Token from Session Storage
      ToastUtil.displaySuccessToast("Logout is Success!");
    },
  },
  extraReducers: (builder) => {
    // Register a User
    builder
      .addCase(UserActions.registerUserAction.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(UserActions.registerUserAction.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          // Ensure to check for msg existence to avoid potential undefined errors
          console.log(action.payload.msg); // Debugging to ensure msg is available
          ToastUtil.displaySuccessToast(action.payload.msg);
        }
      })
      .addCase(UserActions.registerUserAction.rejected, (state, action) => {
        state.loading = false;
        if (action.payload) {
          ToastUtil.displayErrorToast(action.payload.msg);
        }

        if (isRejectedWithValue(action)) {
          state.error = action.error;
        }
      });

    // User Login
    builder
      .addCase(UserActions.loginUserAction.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(UserActions.loginUserAction.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.data;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        TokenUtil.saveToken(action.payload.token); //Save Token to Session Storage
        if (action.payload) {
          ToastUtil.displaySuccessToast(action.payload.msg);
        }
      })
      .addCase(UserActions.loginUserAction.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = {} as IUserView;
        TokenUtil.deleteToken(); //Remove Token from Session Storage
        if (isRejectedWithValue(action)) {
          state.error = action.error;
        }
        if (action.payload) {
          ToastUtil.displayErrorToast(action.payload.msg);
        }
      });

    // Get User's Data
    builder
      .addCase(UserActions.getUserDataAction.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(UserActions.getUserDataAction.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload?.data ?? {};
      })
      .addCase(UserActions.getUserDataAction.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = {} as IUserView;
        if (isRejectedWithValue(action)) {
          state.error = action.error;
        }
      });

    // Get All Users Data
    builder
      .addCase(UserActions.getAllUsersAction.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(UserActions.getAllUsersAction.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.users = action.payload?.data ?? [];
      })
      .addCase(UserActions.getAllUsersAction.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.users = [] as IUserView[];
        if (isRejectedWithValue(action)) {
          state.error = action.error;
        }
      });

    // Update User Roles
    builder
      .addCase(UserActions.updateUserRoleAction.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(UserActions.updateUserRoleAction.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.data;
        ToastUtil.displaySuccessToast(action.payload.msg);
      })
      .addCase(UserActions.updateUserRoleAction.rejected, (state, action) => {
        state.loading = false;
        ToastUtil.displayErrorToast("User Updation is Failed!");
        if (isRejectedWithValue(action)) {
          state.error = action.error;
        }
      });

    // Delete A User
    builder
      .addCase(UserActions.deleteUserAction.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(UserActions.deleteUserAction.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.data;
        ToastUtil.displaySuccessToast(action.payload.msg);
      })
      .addCase(UserActions.deleteUserAction.rejected, (state, action) => {
        state.loading = false;
        ToastUtil.displayErrorToast("User Deletion is Failed!");
        if (isRejectedWithValue(action)) {
          state.error = action.error;
        }
      });

    // Update Profile Picture
    builder
      .addCase(UserActions.updateProfilePicAction.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(
        UserActions.updateProfilePicAction.fulfilled,
        (state, action) => {
          state.loading = false;
          state.user = action.payload.data;
          if (action.payload) {
            ToastUtil.displaySuccessToast(action.payload.msg);
          }
        }
      )
      .addCase(UserActions.updateProfilePicAction.rejected, (state, action) => {
        state.loading = false;
        state.user = {} as IUserView;
        ToastUtil.displayErrorToast(action.payload.msg);
        if (isRejectedWithValue(action)) {
          state.error = action.error;
        }
      });

    // Change Password
    builder
      .addCase(UserActions.changePasswordAction.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(UserActions.changePasswordAction.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.data;
        if (action.payload) {
          ToastUtil.displaySuccessToast(action.payload.msg);
        }
      })
      .addCase(UserActions.changePasswordAction.rejected, (state, action) => {
        state.loading = false;
        state.user = {} as IUserView;
        ToastUtil.displayErrorToast(action.payload.msg);
        if (isRejectedWithValue(action)) {
          state.error = action.error;
        }
      });

    // Create New Address
    builder
      .addCase(UserActions.createNewAddressAction.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(
        UserActions.createNewAddressAction.fulfilled,
        (state, action) => {
          state.loading = false;
          state.address = action.payload.data;
          if (action.payload) {
            ToastUtil.displaySuccessToast(action.payload.msg);
          }
        }
      )
      .addCase(UserActions.createNewAddressAction.rejected, (state, action) => {
        state.loading = false;
        state.address = {} as IAddressView;
        if (action.payload) {
          ToastUtil.displayErrorToast(action.payload.msg);
        }

        if (isRejectedWithValue(action)) {
          state.error = action.error;
        }
      });

    // Update Address
    builder
      .addCase(UserActions.updateAddressAction.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(UserActions.updateAddressAction.fulfilled, (state, action) => {
        state.loading = false;
        state.address = action.payload.data;
        ToastUtil.displaySuccessToast(action.payload.msg);
      })
      .addCase(UserActions.updateAddressAction.rejected, (state, action) => {
        state.loading = false;
        state.address = {} as IAddressView;
        if (action.payload) {
          ToastUtil.displayErrorToast(action.payload.msg);
        }

        if (isRejectedWithValue(action)) {
          state.error = action.error;
        }
      });

    // Get Address
    builder
      .addCase(UserActions.getAddressAction.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(UserActions.getAddressAction.fulfilled, (state, action) => {
        state.loading = false;
        state.address = action.payload.data;
        // ToastUtil.displaySuccessToast(action.payload.msg);
      })
      .addCase(UserActions.getAddressAction.rejected, (state, action) => {
        state.loading = false;
        state.address = {} as IAddressView;
        // ToastUtil.displayErrorToast(action.payload.msg);
        if (isRejectedWithValue(action)) {
          state.error = action.error;
        }
      });

    // Delete Address
    builder
      .addCase(UserActions.deleteAddressAction.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(UserActions.deleteAddressAction.fulfilled, (state, action) => {
        state.loading = false;
        state.address = {} as IAddressView;
        ToastUtil.displayInfoToast(action.payload.msg);
      })
      .addCase(UserActions.deleteAddressAction.rejected, (state, action) => {
        state.loading = false;
        if (action.payload) {
          ToastUtil.displayErrorToast(action.payload.msg);
        }

        if (isRejectedWithValue(action)) {
          state.error = action.error;
        }
      });
  },
});

export const { userLogOutAction } = userSlice.actions;
